import * as THREE from "three";
import type { VfxLightSlot } from "../../../../art/vfx.js";

/**
 * VFX light slots (art_review/vfx.md F5.1): up to six short-lived lights — a strike's flash, a mortar's fireball, a
 * fireball in flight — that light the ground, the towers and the units, WITHOUT a `THREE.PointLight`. A real light
 * is a loop in every PBR fragment and adding one recompiles every material; these are six uniforms shared by every
 * patched material, a loop that stops at `uVfxCount` (0 almost all the time, so the cost is one compare), N·L with a
 * quadratic falloff and a little wrap, added to the direct diffuse.
 *
 * Positions go to the shader in VIEW space so the fragment needs nothing it doesn't already have (`vViewPosition`
 * and the view-space `normal` exist in every MeshStandard/MeshPhysical program), no extra varying.
 *
 * Wiring (the lead does this at merge; the shaders belong to other files):
 *   - hqMaterials.ts, in each MeshStandard material's onBeforeCompile, AFTER its own replaces:  `VfxLights.inject(sh);`
 *   - groundMesh.ts `surfaceMaterial` onBeforeCompile, likewise last:                          `VfxLights.inject(sh);`
 * `inject` anchors on `#include <lights_pars_begin>` and `#include <lights_fragment_end>`, which neither file replaces;
 * it returns false (and changes nothing) for a program without them, e.g. the MeshBasic `_glow` materials.
 * The level calls `VfxLights.sync(vfx.lights, camera)` once a frame after the camera moved; `clear()` on exit.
 */
export const VFX_LIGHT_SLOTS = 6;

/** GLSL declarations: goes after `#include <lights_pars_begin>` in the fragment shader. */
export const VFX_LIGHT_PARS = /* glsl */ `
uniform vec4 uVfxLight[ ${VFX_LIGHT_SLOTS} ];
uniform vec3 uVfxCol[ ${VFX_LIGHT_SLOTS} ];
uniform int uVfxCount;
vec3 vfxLightSum( const in vec3 n, const in vec3 p ) {
  vec3 acc = vec3( 0.0 );
  for ( int i = 0; i < ${VFX_LIGHT_SLOTS}; i ++ ) {
    if ( i >= uVfxCount ) break;
    vec3 L = uVfxLight[ i ].xyz - p;
    float r = uVfxLight[ i ].w;
    float d2 = dot( L, L );
    float f = clamp( 1.0 - d2 / ( r * r ), 0.0, 1.0 );
    if ( f <= 0.0 ) continue;
    float ndl = max( dot( n, L * inversesqrt( d2 + 1e-4 ) ), 0.0 ) * 0.8 + 0.2;
    acc += uVfxCol[ i ] * ( f * f * ndl );
  }
  return acc;
}
`;

/** GLSL applied after `#include <lights_fragment_end>`: the slots add to the direct diffuse, tinted by the albedo. */
export const VFX_LIGHT_APPLY = /* glsl */ `
reflectedLight.directDiffuse += material.diffuseColor * vfxLightSum( normal, - vViewPosition );
`;

const PARS_ANCHOR = "#include <lights_pars_begin>";
const APPLY_ANCHOR = "#include <lights_fragment_end>";

export const VfxLights = {
  /** Shared by every patched program: `inject` puts these very objects into each shader's uniforms. */
  uniforms: {
    uVfxLight: { value: Array.from({ length: VFX_LIGHT_SLOTS }, () => new THREE.Vector4(0, 0, 0, 1)) },
    uVfxCol: { value: Array.from({ length: VFX_LIGHT_SLOTS }, () => new THREE.Vector3()) },
    uVfxCount: { value: 0 },
  },

  /** Patches an onBeforeCompile shader (see the file comment). Returns false when the program has no lighting. */
  inject(shader: { uniforms: Record<string, THREE.IUniform>; fragmentShader: string }): boolean {
    const fs = shader.fragmentShader;
    if (!fs.includes(PARS_ANCHOR) || !fs.includes(APPLY_ANCHOR) || fs.includes("uVfxLight")) return false;
    shader.uniforms.uVfxLight = VfxLights.uniforms.uVfxLight;
    shader.uniforms.uVfxCol = VfxLights.uniforms.uVfxCol;
    shader.uniforms.uVfxCount = VfxLights.uniforms.uVfxCount;
    shader.fragmentShader = fs.replace(PARS_ANCHOR, `${PARS_ANCHOR}\n${VFX_LIGHT_PARS}`).replace(APPLY_ANCHOR, `${APPLY_ANCHOR}\n${VFX_LIGHT_APPLY}`);
    return true;
  },

  /** Copies the live slots into the uniforms, in view space. Call after the camera's final move of the frame. */
  sync(slots: readonly VfxLightSlot[], camera: THREE.Camera): void {
    const pos = VfxLights.uniforms.uVfxLight.value, col = VfxLights.uniforms.uVfxCol.value;
    const view = camera.matrixWorldInverse;
    let n = 0;
    for (const s of slots) {
      if (!s.on || s.k <= 0.001 || n >= VFX_LIGHT_SLOTS) continue;
      const p = pos[n];
      p.set(s.x, s.y, s.z, 1).applyMatrix4(view);
      p.w = s.r;
      const k = s.i * s.k;
      col[n].set(s.cr * k, s.cg * k, s.cb * k);
      n++;
    }
    VfxLights.uniforms.uVfxCount.value = n;
  },

  /** No lights (the level is over; the hub shares the same materials). */
  clear(): void { VfxLights.uniforms.uVfxCount.value = 0; },
};
